<template>
  <div id="sponsor_show">
    <div v-if="page_status === 'new'" class="flex flex-col md:flex-row">
      <div class="w-full md:w-1/2 bg-white rounded py-2 md:p-4 mr-0 md:border md:mr-6">
        <div class="w-full flex items-center" v-if="sponsorFiles.length">
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-8 mr-4 icon-document"><path class="primary" d="M6 2h6v6c0 1.1.9 2 2 2h6v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4c0-1.1.9-2 2-2z"/><polygon class="secondary" points="14 2 20 8 14 8"/></svg>
          </div>
          <div v-for="(s, i) in sponsorFiles" :key="i">
            <a :href="s.url" target="_blank" class="link">{{s.label.replace('-',' ')}}</a>
          </div>
        </div>
        <h1>Sponsorship Levels</h1>
        <p class="py-1 md:pb-4">Select a sponsorship level</p>
        <!-- mobile level selector -->
        <div class="mt-2 w-full md:hidden">
          <div class="relative">
            <select @change="mobileSponsorLevelSelected($event.target.value)" :class="['block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500']">
              <option :value="null" selected disabled>
                Select level
              </option>
              <option v-for="l in levels" :key="l.name" :value="l.name" :disabled="!hasSponsorshipsAvailable(l)">
                {{l.name}}: {{displayAmount(l)}} {{displayAvailable(availableSponsors(l))}}
              </option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
          <div v-if="selected_level" class="mt-2 bg-gray-100 rounded p-1 border">
            <div class="p-2">
              <div>
                <div class="flex justify-between">
                  <p class="text-lg font-semibold">{{displayAmount(selected_level)}}</p>
                  <p class="text-sm">{{displayAvailable(availableSponsors(selected_level))}}</p>
                </div>
                <p class='font-medium text-xl'>{{selected_level.name}}</p>
              </div>
              <div class="mt-1.5 font-light text-gray-800 font- whitespace-pre-wrap">{{selected_level.details}}</div>
            </div>
          </div>
        </div>
        <!-- desktop level selector -->
        <div class="hidden md:block">
          <div v-for="(l,i) in levels" :key="i" class="mb-3">
            <div v-if="!hasSponsorshipsAvailable(l)">
              <div class="bg-red-100 p-3 cursor-mouse rounded-md border-0 cursor-not-allowed">
                <div class='font-medium text-xl text-red-600'>{{l.name}}: {{displayAmount(l)}} {{displayAvailable(availableSponsors(l))}}</div>
                <span class="font-light text-red-600 font- whitespace-pre-wrap">{{l.details}}</span>
              </div>
            </div>
            <div v-else class="cursor-pointer" @click="sponsorLevelSelected(l)">
              <div v-if="l.name == selected_level_name" class="bg-green-200 p-3 border-green-500 rounded-md border-2 ">
                <div class="h-8 flex justify-between items-center">
                  <div class='font-medium text-xl leading-5'>{{l.name}}: {{displayAmount(l)}} {{displayAvailable(availableSponsors(l))}}</div>
                  <div class="pl-2 shrink-0 text-white"><svg viewBox="0 0 24 24" fill="none" class="h-6 w-6"><circle cx="12" cy="12" r="12" fill="#3cb043" opacity="1"></circle><path d="M7 13l3 3 7-7" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></div>
                </div>
                <div class="mt-1.5 font-light text-gray-800 font- whitespace-pre-wrap">{{l.details}}</div>
              </div>
              <div v-else class="p-3 hover:bg-gray-200 border-2 rounded-md hover:border-gray-400">
                <div class="h-8 flex justify-between items-center">
                  <div class='font-medium text-xl leading-5'>{{l.name}}: {{displayAmount(l)}} {{displayAvailable(availableSponsors(l))}}</div>
                </div>
                <div class="mt-1.5 font-light text-gray-800 font- whitespace-pre-wrap">{{l.brief_details? l.brief_details : l.details}}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="md:hidden border-t -mx-3 mt-2"></div>
      <div class="w-full md:w-1/2 bg-white rounded py-2 md:p-4 md:border mt-2 md:mt-0">
        <h1>Sponsor Form</h1>
        <template v-if="eventSponsorConfig.about_text">
          <template v-if="eventSponsorConfig.about_text.length < maxAboutText || showAllAboutText">
            <p class="whitespace-pre-wrap text-sm text-gray-800">{{eventSponsorConfig.about_text}}</p>
            <button class="link" v-if="showAllAboutText" @click="showAllAboutText = false">Click to Show Less</button>
          </template>
          <template v-else>
            <p class="whitespace-pre-wrap text-sm text-gray-800">{{eventSponsorConfig.about_text.substring(0, maxAboutText) + '...'}}</p>
            <button class="link mt-2" @click="showAllAboutText = true">Click to Read More</button>
          </template>
        </template>
        <form-generator v-if="selected_level" ref="sponsor_form" :form_schema="sponsor_schema" :country="event.location_details.country"/>
        <div v-if="(selected_level && (isLevelRange(selected_level) || isOpenLevel(selected_level)))" class="mb-4 p-1">
          <label class="block text-gray-700 text-sm font-bold mb-1" for="sponsorAmount">
            Edit Sponsorship Amount:
          </label>
          <input v-model.number="sponsorshipAmount" id="sponsorAmount" type="number" class="text-input" :min="selected_level.amountMin" :max="selected_level.amountMax">
        </div>
        <div v-if="selected_level">
          <div class="text-2xl font-light">Selected Sponsorship</div>
          <hr class="mb-2"/>
          <div class='text-lg'>{{selected_level_name}}: {{displayAmount(selected_level)}}</div>
          <span class="text-sm font-light text-gray-800 font- whitespace-pre-wrap">{{selected_level.details}}</span>
          <div class="mt-4">
            <template v-if="event.sponsor_settings.pass_on_fees">
              <div>Sponsorship Amount: {{currencySymbol}}{{sponsorshipAmount.toFixed(2)}}</div>
              <div>Processing Fee: {{currencySymbol}}{{credit_card_fees.toFixed(2)}}</div>
            </template>
            <div class="font-bold text-xl">Total Due: {{currencySymbol}}{{total_amount_owed.toFixed(2)}}</div>
          </div>
          <!--Stripe Checkout-->
          <div class="w-full mt-3">
            <div class='flex-col justify-center max-w-md '>
              <button class="btn btn-blue w-full" id="customButton" @click.prevent="openCheckout">Submit Payment</button>
              <div class="flex flex-wrap justify-center mt-1">
                <div class="px-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="42" height="23" viewBox="0 0 42 23"><path d="M20.8 5L18 18h-3.4l2.8-13zm14.1 8.4l1.8-4.9 1 4.9zm3.8 4.6h3.1L39.1 5h-2.9a1.5 1.5 0 0 0-1.4 1l-5 12h3.5l.7-1.9h4.3zm-8.8-4.3c0-3.4-4.7-3.6-4.7-5.1a1.32 1.32 0 0 1 1.4-1.1 6.28 6.28 0 0 1 3.3.6l.6-2.8a8.53 8.53 0 0 0-3.1-.6c-3.3 0-5.7 1.8-5.7 4.3 0 1.9 1.7 2.9 2.9 3.5s1.8 1 1.7 1.6c0 .9-1 1.3-2 1.3a8.53 8.53 0 0 1-3.5-.8l-.6 2.8a9.71 9.71 0 0 0 3.7.7c3.7.1 6-1.7 6-4.4M16 5l-5.4 13H7L4.3 7.6a1.33 1.33 0 0 0-.8-1.1A11.66 11.66 0 0 0 .2 5.4L.3 5H6a1.51 1.51 0 0 1 1.5 1.3l1.4 7.5L12.5 5z" fill="#6b7c93"/></svg>
                </div>
                <div class="px-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="84" height="23" viewBox="0 0 84 23"><path d="M35.25 8.9a2 2 0 0 0-1.8.9 1.94 1.94 0 0 0-1.7-.9 1.66 1.66 0 0 0-1.5.7V9h-1.1v4.9h1.1v-2.7a1.26 1.26 0 0 1 1.3-1.3c.7 0 1.1.5 1.1 1.3v2.7h1.1v-2.7a1.26 1.26 0 0 1 1.3-1.3c.7 0 1.1.5 1.1 1.3v2.7h1.1v-3a1.89 1.89 0 0 0-2-2zm6.9.8a1.81 1.81 0 0 0-1.5-.7 2.42 2.42 0 0 0-2.4 2.6 2.48 2.48 0 0 0 2.4 2.6 1.81 1.81 0 0 0 1.5-.7v.4h1.1V9h-1.1zm-1.4 3.4a1.5 1.5 0 0 1-1.5-1.6 1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-1.5 1.6zM47 11l-.5-.1c-.4-.1-.8-.2-.8-.5s.3-.6.9-.6a3 3 0 0 1 1.5.4l.5-.8a3.94 3.94 0 0 0-2-.5c-1.2 0-2 .6-2 1.6 0 .8.6 1.3 1.6 1.4l.5.1c.6.1.8.3.8.5 0 .4-.4.6-1.1.6a2.17 2.17 0 0 1-1.6-.5l-.5.8a3.77 3.77 0 0 0 2.1.6c1.4 0 2.2-.7 2.2-1.6s-.55-1.2-1.6-1.4zm5.4 2.1a.82.82 0 0 1-.8-.9v-2.1h1.9v-1h-1.9V7.6h-1.1v1.5h-1v1h1v2.1c0 1.3.6 1.9 1.7 1.9a2.46 2.46 0 0 0 1.5-.5l-.4-.9a1.8 1.8 0 0 1-.95.4zm4.4-4.2a2.37 2.37 0 0 0-2.4 2.6 2.39 2.39 0 0 0 2.5 2.6 3.06 3.06 0 0 0 2-.7l-.5-.8a2.39 2.39 0 0 1-1.4.5 1.32 1.32 0 0 1-1.4-1.2h3.6v-.4a2.48 2.48 0 0 0-2.45-2.6zm-1.3 2.2a1.24 1.24 0 0 1 1.3-1.2q1.05 0 1.2 1.2zm5.9-1.5V9h-1.1v4.9h1.1v-2.7a1.18 1.18 0 0 1 1.2-1.3 1.88 1.88 0 0 1 .8.2l.3-1.1a2 2 0 0 0-.8-.1 1.58 1.58 0 0 0-1.55.7zm5 .3a1.85 1.85 0 0 1 1.2.5l.7-.7a2.34 2.34 0 0 0-1.8-.8 2.52 2.52 0 0 0-2.6 2.6 2.47 2.47 0 0 0 2.6 2.6 2.34 2.34 0 0 0 1.8-.8l-.7-.7a1.66 1.66 0 0 1-1.2.5 1.43 1.43 0 0 1-1.4-1.6 1.43 1.43 0 0 1 1.35-1.6zm6.4-.2a1.81 1.81 0 0 0-1.5-.7 2.42 2.42 0 0 0-2.4 2.6 2.48 2.48 0 0 0 2.4 2.6 1.81 1.81 0 0 0 1.5-.7v.4h1.1V9h-1.1zm-1.4 3.4a1.5 1.5 0 0 1-1.5-1.6 1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-1.55 1.6zm5.1-3.5V9h-1.1v4.9h1.1v-2.7a1.18 1.18 0 0 1 1.2-1.3 1.88 1.88 0 0 1 .8.2l.3-1.1a2 2 0 0 0-.8-.1 1.58 1.58 0 0 0-1.55.7zm6.4-3v3a1.81 1.81 0 0 0-1.5-.7 2.42 2.42 0 0 0-2.4 2.6 2.48 2.48 0 0 0 2.4 2.6 1.81 1.81 0 0 0 1.5-.7v.5H84V6.6zm-1.4 6.5a1.5 1.5 0 0 1-1.5-1.6 1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-1.55 1.6zM9 11.6a7.48 7.48 0 0 1 3-6 7.5 7.5 0 1 0 0 12 7.45 7.45 0 0 1-3-6z" fill="#6b7c93"/><path d="M16.5 4.1A7.47 7.47 0 0 0 12 5.61a7.48 7.48 0 0 1 0 12 7.41 7.41 0 0 0 4.5 1.51 7.5 7.5 0 0 0 0-15z" fill="#6b7c93" opacity=".5"/><path d="M15 11.6a7.48 7.48 0 0 0-3-6c-1.82 1.4-4 3.55-4 6s2.18 4.63 4 6a7.45 7.45 0 0 0 3-6z" fill="#6b7c93" opacity=".75"/></svg>
                </div>
                <div class="px-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="59" height="23" viewBox="0 0 59 23"><path fill="#6b7c93" d="M56 7.27L53.13 3h-2.35v7h1.87V5.54L55.57 10h2.26V3H56v4.27zM45.12 7l1-2.35L47 7zm-.19-4l-3.07 7h2.06l.58-1.44h3.26l.62 1.44h2.11l-3.17-7zm-6.14 3.55V6.4a1.61 1.61 0 0 1 1.77-1.77h2.11V3h-2.25a3.22 3.22 0 0 0-3.5 3.4v.15a3.14 3.14 0 0 0 3.4 3.4H41l.76-1.58h-1.1a1.62 1.62 0 0 1-1.82-1.82M34.09 3v7h1.87V3h-1.87zm-3.74 3h-2V4.58h2c.58 0 .86.34.86.72s-.28.67-.86.67m2.73-.72A2.23 2.23 0 0 0 30.59 3h-4v7h1.87V7.56h.67L31.21 10h2.35l-2.3-2.54a2.17 2.17 0 0 0 1.82-2.21M19.42 10h5.9V8.42h-4.03V7.27h3.93V5.68h-3.93v-1.1h4.03V3h-5.9v7zm-5.95-2.59L11.89 3H8.97v7h1.82V4.97L12.61 10h1.68l1.87-5.03V10h1.82V3h-2.92l-1.59 4.41zM3.31 7l1-2.35L5.23 7zm-.24-4L0 10h2.06l.58-1.44H5.9L6.52 10h2.11L5.51 3zm53.68 12.64H55a.56.56 0 1 1 0-1.11h3.12l.74-1.53H55a2.23 2.23 0 0 0-2.49 2.21 2 2 0 0 0 2.25 2.11h1.73a.58.58 0 1 1 0 1.15h-3.72V20h3.74A2.21 2.21 0 0 0 59 17.75a2 2 0 0 0-2.25-2.11m-6.86 0h-1.72a.56.56 0 1 1 0-1.11h3.11L52 13h-3.83a2.23 2.23 0 0 0-2.5 2.21 2 2 0 0 0 2.26 2.11h1.72a.58.58 0 1 1 0 1.15h-3.74V20h3.74a2.21 2.21 0 0 0 2.5-2.25 2 2 0 0 0-2.26-2.11m-10.93 4.31h5.9v-1.58h-4.03v-1.15h3.93v-1.58h-3.93v-1.11h4.03v-1.58h-5.9v7zm-3.69-4.03h-2v-1.39h2c.57 0 .86.34.86.72a.77.77 0 0 1-.86.67m2.73-.71A2.24 2.24 0 0 0 35.51 13h-4v7h1.87v-2.49H34L36.13 20h2.3l-2.3-2.54A2.21 2.21 0 0 0 38 15.21m-10.07.86h-2v-1.54h2a.77.77 0 0 1 .87.77.82.82 0 0 1-.87.77m.19-3.07h-4.07v7h1.87v-2.3h2.16a2.32 2.32 0 0 0 2.54-2.35 2.28 2.28 0 0 0-2.5-2.35m-4.41-.05h-2.39l-1.83 2.11-1.82-2.11h-2.44l3.07 3.45-3.12 3.55h2.4l1.87-2.2 1.91 2.2h2.45l-3.17-3.59 3.07-3.41zm-14.72 7h5.9v-1.58h-4.03v-1.15h3.93v-1.58h-3.93v-1.11h4.03v-1.58h-5.9v7z"/></svg>
                </div>
                <div class="px-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="55" height="23" viewBox="0 0 55 23"><path d="M3.2 7.05H.7V16h2.5a4.45 4.45 0 0 0 3.1-1 4.58 4.58 0 0 0 1.6-3.4 4.35 4.35 0 0 0-4.2-4.5zm2 6.7a3.17 3.17 0 0 1-2.3.7h-.5v-5.9h.5a3.17 3.17 0 0 1 2.3.7 3.1 3.1 0 0 1 .9 2.2 3.17 3.17 0 0 1-.9 2.3zM8.6 16h1.7V7.05H8.6zm5.8-5.5c-1-.4-1.3-.6-1.3-1.1a1.11 1.11 0 0 1 1.2-1 2.1 2.1 0 0 1 1.4.7l.9-1.2a4 4 0 0 0-2.5-1 2.56 2.56 0 0 0-2.7 2.4v.1c0 1.2.5 1.9 2.1 2.4a6.12 6.12 0 0 1 1.2.5 1.05 1.05 0 0 1 .5.9 1.31 1.31 0 0 1-1.2 1.3h-.1a2 2 0 0 1-1.8-1.2L11 14.35a3.4 3.4 0 0 0 3 1.7 2.82 2.82 0 0 0 3-2.7v-.3c-.1-1.2-.6-1.8-2.6-2.6zm3.1 1.1a4.57 4.57 0 0 0 4.5 4.6 5.45 5.45 0 0 0 2.2-.5v-2a2.61 2.61 0 0 1-2 1 2.82 2.82 0 0 1-2.9-2.8v-.2a3 3 0 0 1 2.8-3.1 2.81 2.81 0 0 1 2.1 1v-2a3.84 3.84 0 0 0-2.1-.5 4.33 4.33 0 0 0-4.6 4.45zm20.3 1.5l-2.3-6h-1.8l3.7 9.1h.9L42 7.05h-1.8l-2.4 6zm5 2.9h4.8v-1.5h-3.1v-2.4h3v-1.5h-3v-2h3.1V7.05h-4.8zm11.5-6.3c0-1.7-1.1-2.6-3.1-2.6h-2.5V16h1.7v-3.6h.2l2.3 3.6H55l-2.7-3.7a2.43 2.43 0 0 0 2-2.6zm-3.4 1.5h-.5V8.45h.5c1.1 0 1.6.5 1.6 1.3s-.5 1.4-1.6 1.4z" fill="#6b7c93"/><path d="M29.4 7a4.59 4.59 0 0 0-4.6 4.6 4.74 4.74 0 0 0 4.6 4.7 4.61 4.61 0 0 0 4.7-4.6A4.76 4.76 0 0 0 29.4 7z" fill="#6b7c93" opacity=".5" style="isolation:isolate"/></svg>
                </div>
                <div class="px-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="23" viewBox="0 0 26 23"><path d="M3.16 2A3.14 3.14 0 0 0 0 5.2v8a4.93 4.93 0 0 0 2.67.8 1.72 1.72 0 0 0 1.68-1.6V8H7v4.2c0 1.6-.89 3-3.75 3A17.1 17.1 0 0 1 0 14.8V21h4.84A3.14 3.14 0 0 0 8 17.8V2z" fill="#6b7c93"/><path d="M12.3 2A3.24 3.24 0 0 0 9 5.2V9c.8-.8 2.3-1.3 4.6-1.2a18.66 18.66 0 0 1 2.5.4v1.5a6.81 6.81 0 0 0-2.4-.8 2.64 2.64 0 0 0-2.8 2.2 2.64 2.64 0 0 0 2.2 2.8h.5a6.19 6.19 0 0 0 2.4-.8v1.5a10.86 10.86 0 0 1-2.5.4c-2.3.1-3.7-.4-4.5-1.2v7h4.8a3.16 3.16 0 0 0 3.2-3.2V2z" fill="#6b7c93" opacity=".75" style="isolation:isolate"/><path d="M21.24 2A3.18 3.18 0 0 0 18 5.2V8h5a1.76 1.76 0 0 1 1.82 1.6v.1a1.65 1.65 0 0 1-1.51 1.7 2 2 0 0 1 1.92 1.8 1.89 1.89 0 0 1-1.96 1.8H18v6h4.76A3.18 3.18 0 0 0 26 17.8V2zM20 13.9h2.32a1.14 1.14 0 0 0 .71-1.3 1.2 1.2 0 0 0-.71-.7H20zm2.83-4a1 1 0 0 0-.71-.9H20v1.8h2.12a1 1 0 0 0 .71-.9z" fill="#6b7c93" opacity=".5" style="isolation:isolate"/></svg>
                </div>
                <div class="px-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="27" height="23" viewBox="0 0 27 23"><path d="M15.31 0h-4.6A11.17 11.17 0 0 0 0 11.6 11.42 11.42 0 0 0 11.21 23h4.1A11.72 11.72 0 0 0 27 11.6v-.1A11.61 11.61 0 0 0 15.31 0zm-3.8 21A9.56 9.56 0 0 1 2 11.5 9.56 9.56 0 0 1 11.51 2 9.56 9.56 0 0 1 21 11.5a9.56 9.56 0 0 1-9.49 9.5z" fill="#6b7c93"/><path d="M13 5.2v12.7a6.54 6.54 0 0 0 0-12.7zM5.31 9.9a6.57 6.57 0 0 0 4.7 8V5.2a6.58 6.58 0 0 0-4.7 4.7z" fill="#6b7c93"/></svg>
                </div>
              </div>
              <p class="mt-4 text-gray-500 text-xs text-center">All payments made on this site are safe and secure, using the industry reccomended protocols.</p>
            </div>
          </div>
        </div>
        <div v-if="!selected_level" class="mt-6 bg-blue-100 border border-blue-200 p-4 rounded">
          <p class="mt-1 font-semibold text-center text-blue-900  text-lg">Please select a sponsorship level to finish submitting your information.</p>
        </div>
      </div>
    </div>
    <div v-else-if="page_status === 'success'" class='text-center mt-4'>
      <h1 class='mb-2'>Thank you for your sponsorship contribution!!</h1>
      <p class="mb-4">You will receive an email receipt to the email address you submitted</p>
      <button class="btn btn-blue" @click="page_status='new'">Click here to submit another sponsorship</button>
    </div>
    <div v-else>
      <vue-simple-spinner size="large" :speed="1.1" :line-size="7" message="Submitting Sponsorship Payment" line-fg-color="orange"/>
    </div>
  </div>
</template>

<script>
import {SPONSOR_AMOUNT_TYPES, SPONSOR_NUMBER_TYPES} from '../../components/admin/sponsors/utils'

export default {
  data () {
    return {
      selected_level_name: null,
      sponsor: {
        country: 'US'
      },
      sponsorshipAmount: 0,
      maxAboutText: 225,
      showAllAboutText: false,
      page_status: 'new' // new, loading, success
    }
  },
  methods: {
    hasSponsorshipsAvailable (level) {
      return this.availableSponsors(level) === 'unlimited' || this.availableSponsors(level) > 0
    },
    openCheckout () {
      if (this.$refs.sponsor_form.validateForm()) {
        if (!this.validateEmail(this.sponsor.email)) {
          return alert('Badly formatted Email Address')
        } else if (this.selected_level.amountType === SPONSOR_AMOUNT_TYPES.OPEN && this.sponsorshipAmount < 25) {
          return alert('Minimum sponsorship is $25')
        } else if (this.selected_level.amountType === SPONSOR_AMOUNT_TYPES.RANGE) {
          const hasMax = this.selected_level.amountMax > 0
          const belowMax = this.sponsorshipAmount <= this.selected_level.amountMax
          const aboveMin = this.sponsorshipAmount >= this.selected_level.amountMin
          if (!aboveMin || (hasMax && !belowMax)) {
            return alert(`Sponsorship not in range of ${this.displayAmount(this.selected_level)}`)
          }
        }
        this.sponsor.website = this.formatWebsite(this.sponsor.website)
        this.$store.dispatch('createStripeSession_redirect', this.stripeSessionParams)
      }
    },
    displayAvailable (available) {
      if (available !== 'unlimited') {
        if (available <= 0) {
          return '(Sold Out)'
        }
        return `(${available} Available)`
      } else {
        return ''
      }
    },
    displayAmount (level) {
      if (level.amountType === SPONSOR_AMOUNT_TYPES.RANGE) {
        if (level.amountMax <= level.amountMin) {
          return `${this.currencySymbol}${level.amountMin}+`
        } else {
          return `${this.currencySymbol}${level.amountMin}-${level.amountMax}`
        }
      } else if (level.amountType === SPONSOR_AMOUNT_TYPES.FIXED) {
        return `${this.currencySymbol}${level.amountFixed}`
      }
    },
    formatWebsite (website) {
      if (website) {
        let webRe = /^https?:\/\/(.*)/i
        var hasHttp = website.match(webRe)
        if (hasHttp) {
          return website
        } else {
          return 'https://' + website
        }
      }
      return ''
    },
    mobileSponsorLevelSelected (levelName) {
      const level = this.levels.find(l => l.name === levelName)
      if (level) {
        this.sponsorLevelSelected(level)
      }
    },
    sponsorLevelSelected (level) {
      this.selected_level_name = level.name
      if (this.selected_level.amountType === SPONSOR_AMOUNT_TYPES.RANGE) {
        this.sponsorshipAmount = this.selected_level.amountMin
      } else if (this.selected_level.amountType === SPONSOR_AMOUNT_TYPES.FIXED) {
        this.sponsorshipAmount = this.selected_level.amountFixed
      } else if (this.selected_level.amountType === SPONSOR_AMOUNT_TYPES.OPEN) {
        // min sponsor amount
        this.sponsorshipAmount = 25
      }
    },
    existingSponsorsForLevel (level) {
      return this.existingSponsors.filter(s => s.level === level.name)
    },
    availableSponsors (level) {
      // Calculate how many of this sponsorhip level are avaialble
      if (level.sponsorNumberType === SPONSOR_NUMBER_TYPES.UNLIMITED) {
        return 'unlimited'
      } else if (level.sponsorNumberType === SPONSOR_NUMBER_TYPES.LIMITED) {
        if (level.modifiersEnabled) {
          return (level.sponsorNumber * level.modifiers.length) - this.existingSponsorsForLevel(level).length
        } else {
          return level.sponsorNumber - this.existingSponsorsForLevel(level).length
        }
      } else {
        return 0
      }
    },
    validateEmail (email) {
      const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return email.match(re)
    },
    isLevelRange (level) {
      return level.amountType === SPONSOR_AMOUNT_TYPES.RANGE
    },
    isOpenLevel (level) {
      return level.amountType === SPONSOR_AMOUNT_TYPES.OPEN
    }
  },
  computed: {
    event () {
      return this.$store.state.event.currentEvent
    },
    sponsorFiles () {
      return this.event.files.filter(f => f.show_on_sponsor_form)
    },
    eventSponsorConfig () {
      return this.event.event_sponsor_config
    },
    selected_level () {
      return this.levels.find(l => l.name === this.selected_level_name)
    },
    levels () {
      return this.eventSponsorConfig ? this.eventSponsorConfig.levels : []
    },
    existingSponsors () {
      return this.event.event_sponsors
    },
    availableModifiers () {
      if (this.selected_level && this.selected_level.modifiersEnabled && this.selected_level.sponsorNumberType === SPONSOR_NUMBER_TYPES.LIMITED) {
        return this.selected_level.modifiers.filter(m => {
          const sponsorsWithModifier = this.existingSponsors.filter(s => s.modifier === m && s.level === this.selected_level.name)
          return this.selected_level.sponsorNumber > sponsorsWithModifier.length
        })
      }

      return this.selected_level.modifiers
    },
    sponsor_schema () {
      const baseFields = [
        {
          type: 'input',
          inputType: 'text',
          label: 'Contact Name',
          model: 'contact_name',
          placeholder: '',
          required: true
        },
        {
          type: 'input',
          inputType: 'text',
          label: 'Sponsor Name/Company',
          model: 'sponsor_name',
          placeholder: '',
          required: true
        },
        {
          type: 'select',
          label: 'Country',
          model: 'country',
          placeholder: 'Select country',
          options: this.Localized.countryList.map(({name, abbreviation}) => ({label: name, value: abbreviation})),
          required: true,
          // just a way to not use the existing gray background and to select match other inputs
          classes: 'formGeneratorSelectWhiteBackground formGeneratorSkinnySelectField'
        },
        {
          type: 'input',
          inputType: 'text',
          label: 'Address',
          model: 'address',
          placeholder: '',
          required: true
        },
        {
          type: 'input',
          inputType: 'text',
          label: 'City',
          model: 'city',
          placeholder: '',
          required: true,
          width: 'half'
        },
        {
          type: 'select',
          label: 'State',
          model: 'state',
          placeholder: 'Select state',
          options: this.Localized.getStateList(this.sponsor.country).map(({name, abbreviation}) => ({label: name, value: abbreviation})),
          required: true,
          width: 'half',
          // just a way to not use the existing gray background and to select match other inputs
          classes: 'formGeneratorSelectWhiteBackground formGeneratorSkinnySelectField'
        },
        {
          type: 'input',
          inputType: 'text',
          label: 'Zip Code',
          model: 'zip',
          placeholder: '',
          required: true,
          width: 'half'
        },
        {
          type: 'input',
          inputType: 'text',
          label: 'Phone Number',
          model: 'phone_number',
          placeholder: '',
          required: true,
          width: 'half'
        },
        {
          type: 'input',
          inputType: 'text',
          label: 'Email Address',
          model: 'email',
          placeholder: '',
          required: true
        },
        {
          type: 'input',
          inputType: 'text',
          label: 'Website',
          model: 'website',
          placeholder: '',
          required: false
        },
        {
          type: 'textArea',
          inputType: 'text',
          label: 'Comments',
          model: 'comments',
          placeholder: '',
          required: false
        }
      ]

      let fields = baseFields
      if (this.selected_level && this.selected_level.modifiersEnabled) {
        const modifiers = this.selected_level.modifiers
        fields = [{
          type: 'select',
          label: 'Modifier',
          model: 'modifier',
          placeholder: 'Select one',
          options: modifiers.map(m => ({label: `${m} (${(this.selected_level.sponsorNumber) - this.existingSponsors.filter(s => s.modifier === m && s.level === this.selected_level.name).length} available)`, value: m})),
          required: true,
          disabledOptions: modifiers.filter(m => !this.availableModifiers.includes(m)).map(m => ({label: m, value: m})),
          // just a way to not use the existing gray background and to select match other inputs
          classes: 'formGeneratorSelectWhiteBackground formGeneratorSkinnySelectField'
        }, ...baseFields]
      }

      return [{
        label: 'Sponsor Information',
        model: this.sponsor,
        fields
      }]
    },
    platform_fee () {
      if (this.selected_level) {
        let fee = this.sponsorshipAmount * (this.eventSponsorConfig.technology_percent_fee / 100)
        return fee
      } else {
        return 0
      }
    },
    credit_card_fees () {
      if (this.selected_level) {
        let fee = parseFloat((this.sponsorshipAmount * (this.eventSponsorConfig.cc_percent_fee / 100)).toFixed(2))
        return fee
      } else {
        return 0
      }
    },
    show_credit_card_fees () {
      if (this.selected_level.name) {
        // if (this.event.sponsor_settings.pass_on_fees) {
        if (this.eventSponsorConfig.pass_on_fees) {
          return 0
        } else {
          return this.credit_card_fees
        }
      } else {
        return 0
      }
    },
    exhibitor_credit_card_fees () {
      if (this.selected_level.name) {
        // if (this.event.sponsor_settings.pass_on_fees) {
        if (this.eventSponsorConfig.pass_on_fees) {
          return this.credit_card_fees
        } else {
          return 0
        }
      } else {
        return 0
      }
    },
    total_amount_owed () {
      if (this.selected_level && this.sponsorshipAmount) {
        if (this.eventSponsorConfig.pass_on_fees) {
          return this.sponsorshipAmount + this.credit_card_fees
        } else {
          return this.sponsorshipAmount
        }
      } else {
        return 0
      }
    },
    description () {
      return `Sponsorship for ${this.event.name}`
    },
    stripeSessionParams () {
      return {
        type: 'SPONSORSHIP',
        show_amount: parseFloat(this.sponsorshipAmount.toFixed(2)),
        total_amount: parseFloat(this.total_amount_owed.toFixed(2)),
        show_credit_card_fee: parseFloat(this.show_credit_card_fees.toFixed(2)),
        exhibitor_credit_card_fee: parseFloat(this.exhibitor_credit_card_fees.toFixed(2)),
        platform_fee: parseFloat(this.platform_fee.toFixed(2)),
        event_id: this.event.event_id,
        currency: this.Localized.getCurrencyInfo(this.event.location_details.country).currency,
        event_name: this.event.name,
        event_logo: this.event.event_details.event_card_cover_url,
        event_identifier: this.event.event_identifier,
        email: this.sponsor.email,
        success_url: `${process.env.VUE_APP_ROOT_URL}/shows#/${this.event.event_identifier}/sponsor-thank-you`,
        cancel_url: `${process.env.VUE_APP_ROOT_URL}/shows#/${this.event.event_identifier}/sponsor`,
        sponsor: {
          ...this.sponsor,
          level: this.selected_level_name
        }
      }
    },
    currencySymbol () {
      return this.Localized.getCurrencyInfo(this.event.location_details.country).symbol
    }
  }
}
</script>

<style>
.formGeneratorSelectWhiteBackground:not(.invalid-form-input) {
  background: white !important;
}
.formGeneratorSkinnySelectField {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
</style>
